import React from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import {
  TSubmittalGridRow,
  TMaterialGridRow,
  formatChangeInFloat
} from "../grid-utils";

const RISK_COLORS: Record<string, string> = {
  HIGH: "#dc2626",
  MEDIUM: "#d97706",
  LOW: "#16a34a",
  DELETED: "#6b7280"
};

const RISK_LABELS: Record<string, string> = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  DELETED: "Deleted"
};

function RiskCell({ value }: { value: string }) {
  if (!value) return <span />;
  const color = RISK_COLORS[value] || "#6b7280";
  const label = RISK_LABELS[value] || value;
  return (
    <span className="font-semibold" style={{ color }}>
      {label}
    </span>
  );
}

function fmtDate(v: string | null | undefined) {
  if (!v) return "";
  return DateUtils.format(v, DATE_FORMAT_MMDDYYYY) || "";
}

export type PrintColumn<R> = {
  id: string;
  label: string;
  // Key into the per-feature `field_mapping` rename map. When set, the
  // user's renamed caption from settings overrides `label` at render
  // time. Only the columns that have an equivalent in the log-page
  // rename list carry a `renameKey`; SI-specific columns (delta, risk
  // before/after, etc.) keep their static label.
  renameKey?: string;
  className?: string;
  render: (row: R) => React.ReactNode;
};

export const SUBMITTAL_PRINT_COLUMNS: PrintColumn<TSubmittalGridRow>[] = [
  {
    id: "submittal_sequence_id",
    label: "ID",
    renameKey: "submittal_id",
    className: "whitespace-nowrap font-medium",
    render: (r) => r.submittal_sequence_id
  },
  {
    id: "title",
    label: "Submittal Title",
    renameKey: "title",
    render: (r) => r.title
  },
  {
    id: "spec_section",
    label: "Spec No",
    renameKey: "spec_no",
    render: (r) => r.spec_section
  },
  { id: "spec_name", label: "Spec Name", render: (r) => r.spec_name },
  {
    id: "responsible_contractor",
    label: "Responsible Contractor",
    renameKey: "responsible_contractor",
    render: (r) => r.responsible_contractor
  },
  {
    id: "gc_reviewer",
    label: "GC Reviewer",
    renameKey: "gc_reviewer",
    render: (r) => r.gc_reviewer
  },
  {
    id: "status",
    label: "Status",
    renameKey: "workflow_status",
    render: (r) => r.status
  },
  {
    id: "linked_activity",
    label: "Impacting Activity",
    renameKey: "linked_activities",
    render: (r) => r.linked_activity
  },
  {
    id: "change_in_float",
    label: "Change in Float",
    className: "whitespace-nowrap tabular-nums",
    render: (r) => formatChangeInFloat(r.change_in_float)
  },
  {
    id: "risk_before",
    label: "Before Risk",
    render: (r) => <RiskCell value={r.risk_before} />
  },
  {
    id: "risk_after",
    label: "After Risk",
    render: (r) => <RiskCell value={r.risk_after} />
  },
  {
    id: "final_deadline",
    label: "Final Deadline",
    renameKey: "final_deadline_date",
    render: (r) => fmtDate(r.final_deadline)
  }
];

export const MATERIAL_PRINT_COLUMNS: PrintColumn<TMaterialGridRow>[] = [
  {
    id: "material_sequence_id",
    label: "ID",
    renameKey: "material_id",
    className: "whitespace-nowrap font-medium",
    render: (r) => r.material_sequence_id
  },
  {
    id: "title",
    label: "Material Title",
    renameKey: "name",
    render: (r) => r.title
  },
  {
    id: "spec_section",
    label: "Spec No",
    renameKey: "spec_section_no",
    render: (r) => r.spec_section
  },
  { id: "spec_name", label: "Spec Name", render: (r) => r.spec_name },
  {
    id: "responsible_contractor",
    label: "Responsible Contractor",
    renameKey: "trade_partner",
    render: (r) => r.responsible_contractor
  },
  {
    id: "gc_representative",
    label: "GC Representative",
    renameKey: "gc_representative",
    render: (r) => r.gc_representative
  },
  {
    id: "status",
    label: "Status",
    renameKey: "status",
    render: (r) => r.status
  },
  {
    id: "linked_activity",
    label: "Impacting Activity",
    renameKey: "linked_activities",
    render: (r) => r.linked_activity
  },
  {
    id: "change_in_float",
    label: "Change in Float",
    className: "whitespace-nowrap tabular-nums",
    render: (r) => formatChangeInFloat(r.change_in_float)
  },
  {
    id: "risk_before",
    label: "Before Risk",
    render: (r) => <RiskCell value={r.risk_before} />
  },
  {
    id: "risk_after",
    label: "After Risk",
    render: (r) => <RiskCell value={r.risk_after} />
  },
  {
    id: "final_deadline",
    label: "Final Deadline",
    renameKey: "final_deadline_date",
    render: (r) => fmtDate(r.final_deadline)
  }
];

/**
 * Resolves a column's user-facing label: returns the renamed caption
 * from the per-feature `field_mapping` map if one exists, otherwise the
 * static default `label` from the column descriptor.
 */
export function resolvePrintColumnLabel<R>(
  col: PrintColumn<R>,
  headerMap: Record<string, string> | undefined | null
): string {
  if (col.renameKey && headerMap?.[col.renameKey]) {
    return headerMap[col.renameKey];
  }
  return col.label;
}

// First column (ID) is always retained even if not in visibleColumnIds —
// it's locked-visible in the grid panel and mandatory in the print view.
// `undefined` means no column-visibility info was passed (older URLs):
// fall back to showing all columns for backward compatibility.
export function filterPrintColumns<R>(
  columns: PrintColumn<R>[],
  visibleColumnIds: string[] | undefined
): PrintColumn<R>[] {
  if (!visibleColumnIds) return columns;
  const visibleSet = new Set(visibleColumnIds);
  return columns.filter((c, i) => i === 0 || visibleSet.has(c.id));
}
